import type { InsetEdge } from "./getInsetEdges"
import type { Edge2dComputed, Face2dComputed, Node2d } from "../../mesh"
import type { Identified } from "../../utils"
import { doesPolygon, doSegmentsIntersect, isPointOnSegment } from "../collision"
import { findDistanceBetweenPointAndLine } from "../findDistanceBetweenPointAndLine"
import { edgeToLinearFn, faceToEdges } from "../transmute"

type FilterInsetAreasOptions = {
  dividedAreas: (Face2dComputed & Identified)[]
  area: Face2dComputed
  edges: InsetEdge[]
}

export type FilterInsetAreasProcedure = {
  nodeEdgePairsThatAreTooClose: { node: Node2d; edge: Edge2dComputed }[]
  nodesNotInOriginalArea: {
    node: Node2d
    intersectedEdges: Edge2dComputed[]
    ray: Edge2dComputed
  }[]
}

export const filterInsetAreas = ({
  dividedAreas,
  area,
  edges,
}: FilterInsetAreasOptions): {
  result: (Face2dComputed & Identified)[]
  procedure: FilterInsetAreasProcedure
} => {
  const nodeEdgePairsThatAreTooClose: { node: Node2d; edge: Edge2dComputed }[] =
    []

  const nodesNotInOriginalArea: {
    node: Node2d
    intersectedEdges: Edge2dComputed[]
    ray: Edge2dComputed
  }[] = []

  const filteredAreas = dividedAreas.filter((insetArea) => {
    // const isInsideOriginalArea =
    //   // TODO: Can this be exclusive?
    //   doesPolygon(area).containPolygonInclusive(insetArea)

    const isInsideOriginalArea = insetArea.nodes.every((node) => {
      const originalEdges = faceToEdges(area)
      if (originalEdges.some((edge) => isPointOnSegment(node, edge))) {
        return true
      }
      const ray: Edge2dComputed = {
        p1: node,
        p2: { x: 1e12, y: 1e6 },
      }
      const knownPoints = new Set()
      const intersectedEdges = originalEdges.filter((edge) => {
        if (knownPoints.has(edge.p1) || knownPoints.has(edge.p2)) {
          return false // the node "belongs" to the first edge it is found on
        }
        if (isPointOnSegment(edge.p1, ray)) knownPoints.add(edge.p1)
        if (isPointOnSegment(edge.p2, ray)) knownPoints.add(edge.p2)
        const doesIntersect = doSegmentsIntersect(ray, edge)

        return doesIntersect
      })
      const isInside = intersectedEdges.length % 2 === 1
      if (!isInside) {
        nodesNotInOriginalArea.push({ node, intersectedEdges, ray })
      }

      return isInside
    })

    if (!isInsideOriginalArea) {
      return false
    }

    const edgeData = edges.map(
      (edge): { edge: Edge2dComputed; setbackBox: Face2dComputed } => {
        const setbackBox = {
          nodes: [edge.p1, edge.p2, edge.original.p2, edge.original.p1],
        }

        return { edge, setbackBox }
      },
    )

    const doesNotViolateOriginalSetbacks = insetArea.nodes.every((node) =>
      edgeData.every(({ edge, setbackBox }) => {
        const linearFnFromEdge = edgeToLinearFn(edge)

        if (findDistanceBetweenPointAndLine(node, linearFnFromEdge) < 0.01) {
          return true
        }
        const isWithinSetback = doesPolygon(setbackBox).containPoint(node)
        if (isWithinSetback) {
          nodeEdgePairsThatAreTooClose.push({ node, edge })
        }

        return !isWithinSetback
      }),
    )

    return doesNotViolateOriginalSetbacks
  })

  return {
    result: filteredAreas,
    procedure: { nodeEdgePairsThatAreTooClose, nodesNotInOriginalArea },
  }
}
